import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { signinSchema } from "../../schemas/signinSchema";
import { useUserLogin } from "../../custom-hooks/queries";
import { useAuthStore } from "../../store/useAuthStore";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Eye open and close icons
import CustomButton from "../../components/common/CustomButton";

const SignIn = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const { mutate: login, isPending } = useUserLogin();

  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(signinSchema),
  });

  const onSubmit = (data) => {
    login(
      { email: data.email, password: data.password },
      {
        onSuccess: (response) => {
          // Extract user data from response
          const userData =
            response?.data?.user ||
            response?.data?.vendor ||
            response?.data?.admin;
          const role = userData?.role || response?.data?.role || "";

          setAuth({
            token: response?.data?.token || response?.token,
            role: role,
            user: {
              id: userData?.userId || userData?.id,
              firstName: userData?.firstName || "",
              lastName: userData?.lastName || "",
              name:
                `${userData?.firstName || ""} ${
                  userData?.lastName || ""
                }`.trim() ||
                userData?.email?.split("@")[0] ||
                "",
              email: userData?.email || data.email,
              phone: userData?.phone || "",
              avatar:
                userData?.profileImageUrl ||
                userData?.avatar ||
                userData?.logoUrl ||
                null,
            },
          });

          // toast.success("Login successful!");

          // Navigate based on role
          const normalizedRole = role?.toLowerCase();
          if (normalizedRole === "vendor") {
            navigate("/vendor/dashboard");
          } else if (normalizedRole === "admin") {
            navigate("/admin/dashboard");
          } else {
            navigate("/user/home");
          }
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message || "Login failed. Please try again.",
          );
        },
      },
    );
  };

  return (
    <div className="flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-[500px] border border-gray-200 shadow-sm rounded-2xl p-10 bg-white ml-[25px]">
        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
          Sign In
        </h1>
        <p className="text-gray-600 text-center mb-4">
          Sign in to continue your journey.
        </p>
        {/* Decorative Rectangle */}
        <div className="flex justify-center mb-8">
          <div className="w-[165px] h-[5px] bg-[#16BBD8] rounded"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-800 mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              {...register("email")}
              className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1FADC6] focus:border-transparent transition-colors placeholder-gray-600 ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Email"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-800"
              >
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-[#1FADC6] hover:text-[#16BBD8] font-medium"
              >
                Forgot Password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                {...register("password")}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1FADC6] focus:border-transparent transition-colors placeholder-gray-600 ${
                  errors.password ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <FaEyeSlash className="w-5 h-5 text-gray-600" />
                ) : (
                  <FaEye className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Sign In Button */}
          <CustomButton
            type="submit"
            disabled={isPending}
            loading={isPending}
            className="w-full bg-toucanTeal hover:bg-[#1FADC6] text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#1FADC6] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Signing In..." : "Sign In"}
          </CustomButton>

          {/* Sign Up Link */}
          <p className="text-center text-sm text-gray-600">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-medium text-[#00C2C7] hover:text-[#16BBD8]"
            >
              Sign up
            </Link>
          </p>

          {/* Divider */}
        </form>
      </div>
    </div>
  );
};

export default SignIn;
